import { useState, useEffect, useRef } from 'react';

export default function BenefitsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredBenefit, setHoveredBenefit] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const beneficiaries = [
    {
      title: 'Startups & New Ventures',
      description: 'Launch with a strong identity.',
      icon: 'ri-rocket-2-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Established Businesses',
      description: 'Rebrand or reposition for growth.',
      icon: 'ri-building-2-line',
      color: 'from-purple-600 to-pink-500'
    },
    {
      title: 'Companies Expanding',
      description: 'Enter new markets with cohesive branding.',
      icon: 'ri-global-line',
      color: 'from-pink-500 to-pink-600'
    },
    {
      title: 'Businesses Seeking Clarity',
      description: 'Unify messaging and resolve inconsistencies.',
      icon: 'ri-focus-3-line',
      color: 'from-pink-600 to-orange-500'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url('/assets/benefits-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/70"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Who Can Benefit from 
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Strategic Branding?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Our Branding services are tailored for businesses at any stage:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {beneficiaries.map((beneficiary, index) => (
            <div
              key={index}
              className={`group bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onMouseEnter={() => setHoveredBenefit(index)}
              onMouseLeave={() => setHoveredBenefit(null)}
            >
              <div className="text-center">
                <div className={`w-16 h-16 bg-gradient-to-r ${beneficiary.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse`}>
                  <i className={`${beneficiary.icon} text-2xl text-white`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors duration-300">
                  {beneficiary.title}
                </h3>
                
                <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                  {beneficiary.description}
                </p>

                <div className={`mt-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0`}>
                  <i className="ri-arrow-right-line text-purple-400 text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}